<template>
  <div class="container-fluid py-4">

    <!-- En-tête -->
    <div class="d-flex justify-content-between align-items-center mb-4 header-clinique flex-wrap">
      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-journal-text fs-2 text-primary"></i>
        <div>
          <h2 class="fw-bold mb-0">Journal d'activité</h2>
          <small class="text-muted">
            Qui a encaissé, annulé, modifié ou supprimé quoi, et quand. Ce journal ne peut pas être modifié.
          </small>
        </div>
      </div>
      <button class="btn btn-outline-primary mt-2 mt-md-0" @click="charger" :disabled="chargement">
        <i class="bi bi-arrow-clockwise me-1"></i> Actualiser
      </button>
    </div>

    <!-- Filtres -->
    <div class="card p-3 mb-3 shadow-sm rounded card-clinique">
      <div class="row g-2 align-items-end">
        <div class="col-12 col-md-4">
          <label class="form-label fw-semibold small mb-1">Rechercher</label>
          <input v-model="recherche" type="search" class="form-control form-control-clinique" placeholder="Utilisateur, donnée, description, motif, adresse IP…">
        </div>
        <div class="col-6 col-md-3">
          <label class="form-label fw-semibold small mb-1">Type d'action</label>
          <select v-model="filtreAction" class="form-select form-select-clinique">
            <option value="">Toutes</option>
            <option v-for="(libelle, code) in actions" :key="code" :value="code">{{ libelle }}</option>
          </select>
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">Du</label>
          <input v-model="du" type="date" class="form-control form-control-clinique">
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">Au</label>
          <input v-model="au" type="date" class="form-control form-control-clinique">
        </div>
      </div>
    </div>

    <div v-if="erreur" class="alert alert-danger d-flex justify-content-between align-items-center">
      <span>{{ erreur }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="charger">Réessayer</button>
    </div>

    <!-- Tableau -->
    <div class="card shadow-sm rounded card-clinique">
      <div class="table-responsive" :class="{ 'opacity-50': chargement }">
        <table class="table align-middle mb-0 table-hover modern-table-clinique w-100">
          <thead>
            <tr>
              <th style="width: 150px">Date</th>
              <th style="width: 120px">Action</th>
              <th>Description</th>
              <th style="width: 180px">Utilisateur</th>
              <th style="width: 120px">IP</th>
              <th style="width: 70px"></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="ligne in lignes" :key="ligne.id">
              <tr>
                <td class="text-nowrap">{{ formatDate(ligne.createdAt) }}</td>
                <td>
                  <span class="badge" :class="badge(ligne.action)">{{ actions[ligne.action] || ligne.action }}</span>
                </td>
                <td>
                  {{ ligne.description }}
                  <div v-if="ligne.details?.motif" class="small text-danger mt-1">
                    <i class="bi bi-chat-left-quote me-1"></i> Motif : {{ ligne.details.motif }}
                  </div>
                </td>
                <td>
                  <span v-if="ligne.user">{{ ligne.user.prenom }} {{ ligne.user.nom }}</span>
                  <span v-else-if="ligne.details?.auteur && ligne.details.auteur !== 'système'" class="text-muted" title="Compte de la plateforme ou d'une autre structure">{{ ligne.details.auteur }}</span>
                  <span v-else class="text-muted">Système</span>
                </td>
                <td class="small text-muted">{{ ligne.ip || '—' }}</td>
                <td>
                  <button
                    v-if="ligne.details?.changements"
                    class="btn btn-sm btn-outline-secondary"
                    :title="ouvert === ligne.id ? 'Masquer le détail' : 'Voir le détail'"
                    @click="ouvert = ouvert === ligne.id ? null : ligne.id"
                  >
                    <i class="bi" :class="ouvert === ligne.id ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="ouvert === ligne.id" class="detail-row">
                <td colspan="6">
                  <table class="table table-sm mb-0 bg-white">
                    <thead>
                      <tr><th>Champ</th><th>Avant</th><th>Après</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="(valeur, champ) in ligne.details.changements" :key="champ">
                        <td class="fw-semibold">{{ champ }}</td>
                        <td>{{ Array.isArray(valeur) && valeur.length === 2 ? afficher(valeur[0]) : '—' }}</td>
                        <td>{{ Array.isArray(valeur) && valeur.length === 2 ? afficher(valeur[1]) : afficher(valeur) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </template>
            <tr v-if="!chargement && !lignes.length">
              <td colspan="6" class="text-center text-muted py-4">{{ recherche || filtreAction || du || au ? 'Aucune activité ne correspond à la recherche.' : 'Aucune activité enregistrée.' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="px-3 pb-3">
        <Pagination v-model:page="page" v-model:par-page="parPage" :total="total" />
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import Pagination from '@/components/commun/Pagination.vue'
import { listeServeur } from '@/components/commun/listeServeur'

export default {
  name: 'JournalActivite',
  components: { Pagination },
  mixins: [listeServeur('actions', { tri: 'act.createdAt', sens: 'desc', parPage: 50 })],
  data() {
    return {
      filtreAction: '',
      du: '',
      au: '',
      ouvert: null,
      actions: {
        CREATION: 'Création',
        MODIFICATION: 'Modification',
        ANNULATION: 'Annulation',
        SUPPRESSION: 'Suppression',
        CONNEXION: 'Connexion',
        MOT_DE_PASSE: 'Mot de passe',
        CONSULTATION_DOSSIER: 'Ouverture de dossier',
        COPIE_HORS_LIGNE: 'Copie hors connexion',
        EXPORT: 'Relevé de données'
      }
    }
  },
  watch: {
    filtreAction() { this.rechargerListe() },
    du() { this.rechargerListe() },
    au() { this.rechargerListe() },
  },
  methods: {
    filtresListe() {
      return { action: this.filtreAction, du: this.du, au: this.au }
    },
    formatDate(d) {
      return d ? dayjs(d).format('DD/MM/YYYY HH:mm') : ''
    },
    afficher(v) {
      if (v === null || v === undefined || v === '') return '—'
      if (typeof v === 'object') return JSON.stringify(v)
      return String(v)
    },
    badge(action) {
      return {
        CREATION: 'bg-success',
        MODIFICATION: 'bg-primary',
        ANNULATION: 'bg-warning text-dark',
        SUPPRESSION: 'bg-danger',
        CONNEXION: 'bg-secondary',
        MOT_DE_PASSE: 'bg-info text-dark'
      }[action] || 'bg-light text-dark'
    }
  }
}
</script>

<style scoped>
.header-clinique { padding: 15px 20px; background-color: #e9f5ff; border-radius: 12px; margin-bottom: 20px; }
.card-clinique { background: #f8fafd; border-radius: 12px; border: none; }
.form-control-clinique, .form-select-clinique { border-radius: 10px; border: 1px solid #d1e3f0; padding: 8px 12px; }
.modern-table-clinique thead { background: linear-gradient(90deg, #8fd3f4, #0d6efd); color: #fff; font-weight: 600; }
.detail-row td { background: #f1f6fb; }
</style>
